/**
 * Role-permission matrix, rebuilt on the redesigned screens' shared parts:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/roles-permissions/edit-role.tsx
 *   a role's access as a row of checkboxes with a visible label, and Save/Cancel at the foot;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/roles-permissions/role-card.tsx
 *   the coloured role tile with its name and a count beneath, used here as each column's header;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx
 *   the solid-colour icon tiles on the four figure cards;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   the icon tile + label/value blocks in "How a save works".
 * The header is `PageHero` (components/detail-parts.tsx, from profile/profile-header.tsx).
 *
 * The template's editor is a fixed Read/Write/Delete set for a fixed role list; this matrix has a
 * free-form role list and a free-form permission-code list, both read from the live document — so
 * the columns and rows come from `GET /api/identity/matrix`, and areas are the code's own prefix
 * (`orders.approve` → Orders). Nothing here names a role or decides what a role may do (CLAUDE.md
 * rule 3): the grid shows the document and sends it back.
 *
 * `GET|PUT /api/identity/matrix` (`MatrixController.cs`) — gated by `identity.matrix.administer`.
 * `config/role-matrix.json` is a documented placeholder pending spec §5's real roster (see the
 * frontend repo's CLAUDE.md); this editor works against whatever the matrix currently holds.
 *
 * Unsaved state is a diff between the document as read and the grid as edited, not a flag: toggling
 * a grant back leaves nothing to save, and the review step lists exactly what the PUT will change.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useOutletContext } from "react-router-dom";
import toast from "react-hot-toast";
import {
  PiAsteriskBold as WildcardIcon,
  PiCaretDownBold as CaretDown,
  PiClockCounterClockwiseDuotone as AuditIcon,
  PiGitDiffDuotone as DiffIcon,
  PiInfoDuotone as InfoIcon,
  PiKeyDuotone as KeyIcon,
  PiLightningDuotone as LiveIcon,
  PiListPlusDuotone as AddRowIcon,
  PiMagnifyingGlassBold as SearchIcon,
  PiMinusCircleDuotone as RevokeIcon,
  PiPencilSimpleDuotone as PencilIcon,
  PiPlusBold as PlusIcon,
  PiPlusCircleDuotone as GrantIcon,
  PiShieldCheckDuotone as ShieldIcon,
  PiStackDuotone as StackIcon,
  PiUserPlusDuotone as AddRoleIcon,
  PiUsersThreeDuotone as RolesIcon,
} from "react-icons/pi";
import { Avatar, Badge, Button, Checkbox, Input, Text } from "rizzui";
import { ConfirmDialog, EmptyState, ErrorSurface, MetricCard, Skeleton, WidgetCard, cn } from "@steelyard/theme";
import { CardTitle, InfoBlock, PageHero } from "../../components/detail-parts";
import { roleName, sentenceCase } from "../../lib/format";
import { ALL_PERMISSIONS, can } from "../../lib/permissions";
import { getMatrix, putMatrix, type MatrixDocument } from "../../lib/services/matrix";
import type { AccountSummary, PermissionState } from "../../types";

type Roles = Record<string, string[]>;
type Change = { role: string; code?: string; kind: "new-role" | "granted" | "revoked" };

/** Decorative only — which colour a column gets follows its position, never what the role is. */
const ROLE_COLORS = ["secondary", "info", "success"] as const;
const AREA_TONES = [
  "bg-secondary-lighter text-secondary-dark",
  "bg-blue-lighter text-blue-dark",
  "bg-green-lighter text-green-dark",
  "bg-orange-lighter text-orange-dark",
];
const WILDCARD = "*";

const roleLabel = (role: string) => roleName(role);
const areaOf = (code: string) => (code === WILDCARD ? WILDCARD : code.split(".")[0]!);
const areaLabel = (area: string) => (area === WILDCARD ? "Wildcard" : sentenceCase(area));

/** `pricing.tiers.readAny` → "Tiers read any" — the area prefix is the group heading already. */
function permissionLabel(code: string): string {
  if (code === WILDCARD) return "Every permission, including ones not yet invented";
  const words = code.split(".").slice(1).join(" ").replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : code;
}

/** What a save would change: each grant added or removed, per role, plus roles that did not exist. */
function diffMatrix(before: Roles, after: Roles): Change[] {
  const changes: Change[] = [];
  for (const role of [...new Set([...Object.keys(before), ...Object.keys(after)])].sort()) {
    const was = new Set(before[role] ?? []);
    const now = new Set(after[role] ?? []);
    if (!(role in before)) changes.push({ role, kind: "new-role" });
    for (const code of [...now].sort()) if (!was.has(code)) changes.push({ role, code, kind: "granted" });
    for (const code of [...was].sort()) if (!now.has(code)) changes.push({ role, code, kind: "revoked" });
  }
  return changes;
}

export default function RoleMatrixEditor() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const permitted = can(permissionState, "identity.matrix.administer");
  const mayReadAudit = can(permissionState, "identity.audit.read");

  const [doc, setDoc] = useState<MatrixDocument | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [roles, setRoles] = useState<Roles>({});
  const [extraRows, setExtraRows] = useState<string[]>([]);
  const [newRole, setNewRole] = useState("");
  const [newPermission, setNewPermission] = useState("");
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [confirming, setConfirming] = useState(false);
  const [filter, setFilter] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!permitted) return;
    let cancelled = false;
    getMatrix().then(
      (fresh) => {
        if (cancelled) return;
        setDoc(fresh);
        setRoles(fresh.roles);
      },
      (err) => {
        if (!cancelled) setLoadError(err);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [permitted]);

  const roleNames = useMemo(() => Object.keys(roles).sort(), [roles]);
  const permissionCodes = useMemo(() => {
    // Codes held only through super_admin's wildcard are granted to no role, so the document alone
    // never lists them; the app's known codes are rows too, or they could never be granted.
    const set = new Set<string>([...ALL_PERMISSIONS, ...extraRows]);
    for (const granted of Object.values(roles)) granted.forEach((p) => set.add(p));
    return [...set].sort();
  }, [roles, extraRows]);
  const areas = useMemo(() => [...new Set(permissionCodes.map(areaOf))].sort(), [permissionCodes]);
  const changes = useMemo(() => (doc ? diffMatrix(doc.roles, roles) : []), [doc, roles]);

  if (!permitted) {
    return (
      <ErrorSurface
        error={{ title: "Not permitted", status: 403, detail: "The role matrix is Super Admin only, gated by identity.matrix.administer." }}
        action="administer the role matrix"
      />
    );
  }
  if (loadError) return <ErrorSurface error={loadError} action="read the role matrix" />;
  if (!doc) {
    return (
      <div aria-busy="true" aria-label="Loading the matrix" className="flex flex-col gap-6">
        <Skeleton className="h-52 w-full rounded-2xl" />
        <Skeleton className="h-28 w-full rounded-xl" />
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    );
  }

  function toggle(role: string, permission: string) {
    setRoles((prev) => {
      const current = new Set(prev[role] ?? []);
      if (current.has(permission)) current.delete(permission);
      else current.add(permission);
      return { ...prev, [role]: [...current].sort() };
    });
  }

  function addRole() {
    const name = newRole.trim();
    if (!name) return;
    if (roleNames.includes(name)) {
      toast.error(`${name} is already a column in the matrix`);
      return;
    }
    setRoles((prev) => ({ ...prev, [name]: [] }));
    setNewRole("");
  }

  function addPermissionRow() {
    const code = newPermission.trim();
    if (!code) return;
    if (permissionCodes.includes(code)) {
      toast.error(`${code} is already a row in the matrix`);
      return;
    }
    setExtraRows((prev) => [...prev, code]);
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.delete(areaOf(code));
      return next;
    });
    setNewPermission("");
  }

  async function handleSave() {
    setConfirming(false);
    setSaveError(null);
    setBusy(true);
    try {
      const fresh = await putMatrix({ version: doc!.version + 1, roles }).then(getMatrix);
      setDoc(fresh);
      setRoles(fresh.roles);
      setExtraRows([]);
      toast.success(`Role matrix saved — now version ${fresh.version}`);
    } catch (err) {
      setSaveError(err);
      toast.error("Could not save the role matrix");
    } finally {
      setBusy(false);
    }
  }

  function handleRevert() {
    setRoles(doc!.roles);
    setExtraRows([]);
    setSaveError(null);
  }

  const needle = filter.trim().toLowerCase();
  const visibleCodes = needle ? permissionCodes.filter((code) => code.toLowerCase().includes(needle) || permissionLabel(code).toLowerCase().includes(needle)) : permissionCodes;
  const visibleAreas = areas.filter((area) => visibleCodes.some((code) => areaOf(code) === area));
  const wildcardRoles = roleNames.filter((role) => (roles[role] ?? []).includes(WILDCARD));
  const newRoles = changes.filter((change) => change.kind === "new-role").length;
  const rolesTouched = new Set(changes.map((change) => change.role)).size;

  return (
    <div className="flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Administration" }, { name: "Role Matrix" }]}
        icon={<ShieldIcon aria-hidden className="size-9" />}
        title="Role-Permission Matrix"
        description="Who can do what. Six services read this document live — a save reaches them without a release, and is recorded in the audit trail."
        meta={
          <>
            <Badge variant="flat" color="secondary" rounded="pill" className="gap-1.5">
              <StackIcon aria-hidden className="size-3.5" />
              Version {doc.version}
            </Badge>
            {changes.length > 0 ? (
              <Badge variant="flat" color="warning" rounded="pill" className="gap-1.5">
                <PencilIcon aria-hidden className="size-3.5" />
                {changes.length} unsaved {changes.length === 1 ? "change" : "changes"}
              </Badge>
            ) : (
              <Badge variant="flat" color="success" rounded="pill">
                Matches what is saved
              </Badge>
            )}
            <Badge variant="outline" rounded="pill" className="gap-1.5 border-muted text-gray-700">
              <LiveIcon aria-hidden className="size-3.5" />
              Read live, no release needed
            </Badge>
          </>
        }
        actions={
          mayReadAudit ? (
            <Link to="/audit">
              <Button as="span" variant="outline">
                <AuditIcon aria-hidden className="me-1.5 size-4" />
                Audit trail
              </Button>
            </Link>
          ) : null
        }
      />

      {saveError ? <ErrorSurface error={saveError} action="save the role matrix" /> : null}

      <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-4">
        <MetricCard
          title="Roles"
          metric={roleNames.length}
          icon={<RolesIcon aria-hidden className="size-6" />}
          iconClassName="bg-secondary text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">{newRoles > 0 ? `${newRoles} new, not saved yet` : "Columns in the matrix"}</Text>}
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Permission codes"
          metric={permissionCodes.length}
          icon={<KeyIcon aria-hidden className="size-6" />}
          iconClassName="bg-blue text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">Across {areas.length} {areas.length === 1 ? "area" : "areas"}</Text>}
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Wildcard grants"
          metric={wildcardRoles.length}
          icon={<WildcardIcon aria-hidden className="size-6" />}
          iconClassName="bg-red text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">Roles holding every permission, future ones included</Text>}
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Next save"
          metric={`Version ${doc.version + 1}`}
          icon={<StackIcon aria-hidden className="size-6" />}
          iconClassName="bg-green text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">Replaces the whole document at once</Text>}
          className="transition-shadow hover:shadow-md"
        />
      </div>

      {/* ── The grid ─────────────────────────────────────────────────────────────────────── */}
      <WidgetCard
        rounded="xl"
        className="overflow-hidden p-0 lg:p-0"
        headerClassName="flex flex-wrap items-center justify-between gap-3 px-5 pb-4 pt-5 lg:px-7 lg:pt-6"
        title={
          <CardTitle icon={<ShieldIcon aria-hidden className="size-5" />} tile="bg-secondary-lighter text-secondary-dark">
            Grants
          </CardTitle>
        }
        action={
          <Input
            type="search"
            size="sm"
            aria-label="Find a permission"
            placeholder="Find a permission…"
            prefix={<SearchIcon aria-hidden className="size-3.5 text-gray-500" />}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-64"
          />
        }
      >
        {roleNames.length === 0 || permissionCodes.length === 0 ? (
          <div className="px-5 pb-6 lg:px-7">
            <EmptyState title={roleNames.length === 0 ? "No roles yet" : "No permission codes yet"}>
              {roleNames.length === 0 ? "Add a role below to grant it anything." : "Add a permission code below, then grant it to a role."}
            </EmptyState>
          </div>
        ) : visibleCodes.length === 0 ? (
          <div className="px-5 pb-6 lg:px-7">
            <EmptyState title="Nothing matches">No permission code or label contains “{filter.trim()}”.</EmptyState>
          </div>
        ) : (
          <div className="max-h-[70vh] overflow-auto border-t border-muted">
            <table className="w-full border-separate border-spacing-0 text-sm">
              <caption className="sr-only">Which role holds which permission. Each checkbox grants or revokes one permission for one role.</caption>
              <thead>
                <tr>
                  <th scope="col" className="sticky left-0 top-0 z-20 min-w-64 border-b border-e border-muted bg-gray-50 px-4 py-3 text-left align-bottom text-xs font-semibold uppercase tracking-wider text-gray-500 dark:bg-gray-100">
                    Permission
                  </th>
                  {roleNames.map((role, index) => {
                    const held = roles[role] ?? [];
                    const isNew = !(role in doc.roles);
                    return (
                      <th key={role} scope="col" className="sticky top-0 z-10 min-w-32 border-b border-muted bg-gray-50 px-3 py-3 align-bottom dark:bg-gray-100">
                        <div className="flex flex-col items-center gap-1.5 text-center">
                          <Avatar name={roleLabel(role)} color={ROLE_COLORS[index % ROLE_COLORS.length]} size="sm" rounded="md" />
                          <span className="text-xs font-semibold text-gray-900">{roleLabel(role)}</span>
                          <code className="font-mono text-[10px] font-normal text-gray-500">{role}</code>
                          {held.includes(WILDCARD) ? (
                            <Badge variant="flat" color="danger" size="sm" rounded="pill">
                              All (*)
                            </Badge>
                          ) : (
                            <span className="text-[11px] font-normal text-gray-500">{held.length} granted</span>
                          )}
                          {isNew ? (
                            <Badge variant="flat" color="warning" size="sm" rounded="pill">
                              New
                            </Badge>
                          ) : null}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              {visibleAreas.map((area) => {
                const codes = visibleCodes.filter((code) => areaOf(code) === area);
                const isCollapsed = collapsed.has(area) && !needle;
                const tone = area === WILDCARD ? "bg-red-lighter text-red-dark" : AREA_TONES[areas.indexOf(area) % AREA_TONES.length];
                return (
                  <tbody key={area}>
                    <tr>
                      <td colSpan={roleNames.length + 1} className="border-b border-muted bg-gray-50/60 px-4 py-2 dark:bg-gray-100/40">
                        <button
                          type="button"
                          aria-expanded={!isCollapsed}
                          onClick={() =>
                            setCollapsed((prev) => {
                              const next = new Set(prev);
                              if (next.has(area)) next.delete(area);
                              else next.add(area);
                              return next;
                            })
                          }
                          className="sticky left-4 flex items-center gap-2.5 rounded-md py-0.5 text-sm"
                        >
                          <CaretDown aria-hidden className={cn("size-3.5 text-gray-500 transition-transform", isCollapsed && "-rotate-90")} />
                          <span className={cn("rounded-md px-2 py-0.5 text-xs font-semibold", tone)}>{areaLabel(area)}</span>
                          <span className="text-xs text-gray-500">
                            {codes.length} {codes.length === 1 ? "permission" : "permissions"}
                          </span>
                        </button>
                      </td>
                    </tr>
                    {isCollapsed
                      ? null
                      : codes.map((code) => {
                          const unsavedRow = extraRows.includes(code) && !roleNames.some((role) => (roles[role] ?? []).includes(code));
                          return (
                            <tr key={code} className="group">
                              <th scope="row" className="sticky left-0 z-[5] border-b border-e border-muted bg-gray-0 px-4 py-2.5 text-left font-normal group-hover:bg-gray-50 dark:bg-gray-50 dark:group-hover:bg-gray-100">
                                <span className="block font-medium text-gray-900">{permissionLabel(code)}</span>
                                <code className="font-mono text-[11px] text-gray-500">{code}</code>
                                {unsavedRow ? (
                                  <Badge variant="flat" color="warning" size="sm" rounded="pill" className="ms-2">
                                    Saved once granted
                                  </Badge>
                                ) : null}
                              </th>
                              {roleNames.map((role) => (
                                <GrantCell
                                  key={role}
                                  role={role}
                                  code={code}
                                  granted={(roles[role] ?? []).includes(code)}
                                  wasGranted={(doc.roles[role] ?? []).includes(code)}
                                  viaWildcard={code !== WILDCARD && (roles[role] ?? []).includes(WILDCARD)}
                                  disabled={busy}
                                  onToggle={() => toggle(role, code)}
                                />
                              ))}
                            </tr>
                          );
                        })}
                  </tbody>
                );
              })}
            </table>
          </div>
        )}
      </WidgetCard>

      {/* ── Extend it, and what saving means ─────────────────────────────────────────────── */}
      <div className="grid gap-6 lg:grid-cols-2 2xl:grid-cols-3">
        <WidgetCard
          rounded="xl"
          title={
            <CardTitle icon={<AddRoleIcon aria-hidden className="size-5" />} tile="bg-blue-lighter text-blue-dark">
              Add a role
            </CardTitle>
          }
          headerClassName="items-center"
        >
          <form
            className="mt-5 flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              addRole();
            }}
          >
            <Input label="Role name" placeholder="lower_snake_case" value={newRole} onChange={(e) => setNewRole(e.target.value)} className="flex-1" />
            <Button type="submit" variant="outline" disabled={!newRole.trim() || busy}>
              <PlusIcon aria-hidden className="me-1.5 size-3.5" />
              Add
            </Button>
          </form>
          <Text className="mt-3 text-xs text-gray-500">A new column starts with nothing granted. lower_snake_case, 1–64 characters — the service refuses anything else.</Text>
        </WidgetCard>

        <WidgetCard
          rounded="xl"
          title={
            <CardTitle icon={<AddRowIcon aria-hidden className="size-5" />} tile="bg-green-lighter text-green-dark">
              Add a permission code
            </CardTitle>
          }
          headerClassName="items-center"
        >
          <form
            className="mt-5 flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              addPermissionRow();
            }}
          >
            <Input label="Permission code" placeholder="area.action" value={newPermission} onChange={(e) => setNewPermission(e.target.value)} className="flex-1" />
            <Button type="submit" variant="outline" disabled={!newPermission.trim() || busy}>
              <PlusIcon aria-hidden className="me-1.5 size-3.5" />
              Add
            </Button>
          </form>
          <Text className="mt-3 text-xs text-gray-500">
            A permission with no role checked is not saved — it only becomes part of the matrix once granted to at least one role.
          </Text>
        </WidgetCard>

        <WidgetCard
          rounded="xl"
          className="lg:col-span-2 2xl:col-span-1"
          title={
            <CardTitle icon={<InfoIcon aria-hidden className="size-5" />} tile="bg-orange-lighter text-orange-dark">
              How a save works
            </CardTitle>
          }
          headerClassName="items-center"
        >
          <div className="mt-5 flex flex-col gap-4">
            <InfoBlock icon={<StackIcon aria-hidden className="size-4" />} tile="bg-secondary-lighter text-secondary-dark" label="Replaces the whole matrix">
              <Text className="text-sm text-gray-700">Every role’s grants are written at once. There is no undo beyond editing it back by hand.</Text>
            </InfoBlock>
            <InfoBlock icon={<AuditIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Recorded">
              <Text className="text-sm text-gray-700">Against the signed-in account in the audit trail, before and after. Every service picks it up on its next read.</Text>
            </InfoBlock>
            <InfoBlock icon={<WildcardIcon aria-hidden className="size-4" />} tile="bg-red-lighter text-red-dark" label="Wildcards">
              <Text className="text-sm text-gray-700">
                The single wildcard <code className="font-mono">*</code> is supported. A prefix wildcard like <code className="font-mono">orders.*</code> is refused by the service.
              </Text>
            </InfoBlock>
          </div>
        </WidgetCard>
      </div>

      {/* ── Unsaved changes: always in reach while the grid scrolls ──────────────────────── */}
      {changes.length > 0 ? (
        <div role="status" className="sticky bottom-4 z-30 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-orange/40 bg-gray-0 px-5 py-3 shadow-lg dark:bg-gray-50">
          <div className="flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-orange-lighter text-orange-dark">
              <DiffIcon aria-hidden className="size-5" />
            </span>
            <div>
              <Text className="text-sm font-semibold text-gray-900">
                {changes.length} unsaved {changes.length === 1 ? "change" : "changes"} across {rolesTouched} {rolesTouched === 1 ? "role" : "roles"}
              </Text>
              <Text className="text-xs text-gray-500">Nothing reaches the services until you save.</Text>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRevert} disabled={busy}>
              Revert
            </Button>
            <Button onClick={() => setConfirming(true)} isLoading={busy}>
              Review and save
            </Button>
          </div>
        </div>
      ) : null}

      {confirming ? (
        <ConfirmDialog
          title="Replace the whole role matrix?"
          consequence="This overwrites every role's permissions at once, takes effect for every service on their next read, and has no undo beyond editing it back by hand — only what is on screen now is kept."
          confirmLabel={busy ? "Saving…" : `Save version ${doc.version + 1}`}
          cancel={
            <Button type="button" variant="outline" size="lg" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          }
          action={handleSave}
        >
          <ChangeList changes={changes} />
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

function GrantCell({
  role,
  code,
  granted,
  wasGranted,
  viaWildcard,
  disabled,
  onToggle,
}: {
  role: string;
  code: string;
  granted: boolean;
  wasGranted: boolean;
  viaWildcard: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  const changed = granted !== wasGranted;
  return (
    <td className={cn("border-b border-muted px-3 py-2.5 text-center", changed && "bg-orange-lighter/40 dark:bg-orange-lighter/20")}>
      <div className="flex flex-col items-center gap-0.5">
        <Checkbox
          aria-label={`${roleLabel(role)}: ${code}${changed ? (granted ? " (granting, unsaved)" : " (revoking, unsaved)") : ""}`}
          checked={granted}
          onChange={onToggle}
          disabled={disabled}
        />
        {changed ? (
          <span aria-hidden className="text-[10px] font-semibold text-orange-dark">
            {granted ? "Granting" : "Revoking"}
          </span>
        ) : viaWildcard && !granted ? (
          <span className="text-[10px] text-gray-500">via *</span>
        ) : null}
      </div>
    </td>
  );
}

/** The review step: every grant the save adds or removes, grouped by role, in words as well as colour. */
function ChangeList({ changes }: { changes: Change[] }) {
  const byRole = new Map<string, Change[]>();
  for (const change of changes) byRole.set(change.role, [...(byRole.get(change.role) ?? []), change]);

  const line = (key: string, icon: ReactNode, verb: string, code: string | undefined, tone: string) => (
    <li key={key} className="flex items-center gap-2 text-sm">
      <span className={cn("flex size-5 shrink-0 items-center justify-center rounded", tone)}>{icon}</span>
      <span className="text-gray-700">{verb}</span>
      {code ? <code className="font-mono text-xs text-gray-900">{code}</code> : null}
    </li>
  );

  return (
    <div className="mt-4 max-h-72 overflow-y-auto rounded-lg border border-muted">
      {[...byRole.entries()].map(([role, list]) => (
        <div key={role} className="border-b border-muted px-4 py-3 last:border-b-0">
          <Text className="text-sm font-semibold text-gray-900">
            {roleLabel(role)} <code className="font-mono text-xs font-normal text-gray-500">{role}</code>
          </Text>
          <ul className="mt-2 flex flex-col gap-1.5">
            {list.map((change) =>
              change.kind === "new-role"
                ? line("new-role", <AddRoleIcon aria-hidden className="size-3.5" />, "New role", undefined, "bg-blue-lighter text-blue-dark")
                : change.kind === "granted"
                  ? line(`g-${change.code}`, <GrantIcon aria-hidden className="size-3.5" />, "Grant", change.code, "bg-green-lighter text-green-dark")
                  : line(`r-${change.code}`, <RevokeIcon aria-hidden className="size-3.5" />, "Revoke", change.code, "bg-red-lighter text-red-dark"),
            )}
          </ul>
        </div>
      ))}
    </div>
  );
}
